

import { FC, useEffect, useContext, useState, useLayoutEffect } from "react";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { ToastTypes } from "src/types";
import { AuthContext } from "src/context/authContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import FooterText from "src/components/footerText";
import { CaseListingType, ProvidersObject, StatisticsApiResponse, statisticsErrorAndData } from "./types";
import CaseVolumeHorizontal from "src/components/manufacturer/caseVolumeHorizontal";
import PieChartTerritories from "src/components/manufacturer/pieChartTerritories";
import Practices from "src/components/manufacturer/practices";
import Provider from "src/components/manufacturer/provider";
import CaseDetail from "src/components/manufacturer/caseDetail";
import { StatisticsSearchProps, TerritoriesSearchProps } from "src/components/manufacturer/statistics/types";
import TerritoriesFilter from "src/components/manufacturer/territoriesFilter";
import OngoingTreatment from "src/components/manufacturer/ongoingTreatments";
import { onGoingTreatmentType } from "../territories/types";
import ContactAndSafetyInfo from "src/components/contactAndSafetyInfo";
import { TextAlignProps } from "src/components/contactAndSafetyInfo/types";
// import OngoingTreatment from "src/components/manufacturer/ongoingTreatment";
// import CaseFilter from "src/components/manufacturer/statistics";

const Region: FC = (): JSX.Element => {

  const { destroySession } = useContext(AuthContext)
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const navigate = useNavigate();
  const params = useParams();
  let [searchParams, updateSearchParams] = useSearchParams();

  useLayoutEffect(()=>{
    setLoader();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const [queryParams, changeQueryParams] = useState<TerritoriesSearchProps>({
    start_date:
      searchParams.get("start_date") ||
      format(new Date().setDate(new Date().getDate() - 60), "yyyy-MM-dd"),
    end_date:
      searchParams.get("end_date") ||
      format(new Date().setDate(new Date().getDate()+1), "yyyy-MM-dd"),
    period: searchParams.get('period') || "day",
    filter: searchParams.get("filter") || "",
    filter_type: searchParams.get("filter_type") || "",
    type: searchParams.get('type') ?? "regions",
    role: searchParams.get('role') ?? "regional_manager",
    id: searchParams.get('id') ?? params?.Id ?? "0",
    case_id: searchParams.get('case_id') || "",
    search_str:searchParams.get('search_str') || "",
  });



  const { loading: statisticsLoading, data: statisticsData, error: errorAndData, mutate: getStatsData } =
    useFetch<StatisticsApiResponse>(APIEndpoints.MANUFACTURE_STATISTICS, queryParams, {}, false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => setLoader(), [])

  useEffect(() => {
    if (errorAndData) {
      const { error, code } = errorAndData as unknown as statisticsErrorAndData;
      hideLoader();
      showToast([error || `Can't fetch statistics right now. Please try again in a while.`], ToastTypes.ERROR);

      if (code === 401) {
        destroySession();
        navigate(`/users/sign_in`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorAndData]);

  useEffect(() => {
    if (statisticsData) {
      hideLoader();
      if (statisticsData.code === 200) {
        showToast([statisticsData.message], ToastTypes.SUCCESS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statisticsData]);

  const updateQueryParamsAndCallAPI = (
    updatedQueries: TerritoriesSearchProps | StatisticsSearchProps,
    isReset: boolean = false
  ): void => {
    if (isReset) {
      updateSearchParams({
        id: searchParams.get('id') ?? params?.Id ?? "0",
        type: searchParams.get('type') ?? "regions",
        role: searchParams.get('role') ?? "regional_manager",
        period: "day",
      });
      changeQueryParams({
        start_date: format(
          new Date().setDate(new Date().getDate() - 60),
          "yyyy-MM-dd"
        ),
        end_date: format(
          new Date().setDate(new Date().getDate()+1),
          "yyyy-MM-dd"
        ),
        period: "day",
        filter: "",
        filter_type: "",
        case_id:"",
        search_str:"",
        type: searchParams.get('type') ?? "regions",
        role: searchParams.get('role') ?? "regional_manager",
        id: searchParams.get('id') ?? params?.Id ?? "0",
      });
    } else {
      const params = new URLSearchParams({ ...queryParams, ...updatedQueries });
      updateSearchParams(params.toString(), { replace: false });
      changeQueryParams({ ...queryParams, ...updatedQueries });
    }

    setLoader();
    getStatsData({}, {}, updatedQueries);
  };

  useEffect(() => {
    const updatedParams = {
      start_date: searchParams.get('start_date') || format((new Date()).setDate((new Date()).getDate() - 60), "yyyy-MM-dd"),
      end_date: searchParams.get('end_date') || format((new Date()).setDate((new Date()).getDate() + 1), "yyyy-MM-dd"),
      period: searchParams.get('period') || "day",
      filter: searchParams.get('filter') || "",
      filter_type: searchParams.get('filter_type') || "",
      type: searchParams.get('type') ?? "regions",
      role: searchParams.get('role') ?? "regional_manager",
      id: searchParams.get('id') ?? params?.Id ?? "0",
      case_id: searchParams.get('case_id') || "",
      search_str: searchParams.get('search_str') || "",
    };
    if (!statisticsLoading) {
      setLoader();
      changeQueryParams(updatedParams);
      getStatsData({}, {}, updatedParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);



  return (
    <div className="container-fluid auth-page mb-4 overflow-x-hidden">
      <div className="row justify-content-end mt-3">
        <div className="col-12 col-md-6">
          <ContactAndSafetyInfo textColor="black" alignText={TextAlignProps.TEXT_END} />
        </div>
      </div>
      <TerritoriesFilter
        defaultQueryParams={queryParams}
        updateQueryParams={updateQueryParamsAndCallAPI}
      />
      <div className="mt-3">
        {statisticsData && <CaseVolumeHorizontal StatisticsObjectType={statisticsData.data} />}
      </div>
      <div className="mb-3">
        {statisticsData && <PieChartTerritories StatisticsObjectType={statisticsData.data} />}
      </div>

      <div className="row">
        <div className="col-12 col-lg-6 d-flex mb-lg-0 mb-3">
          <Practices/>
        </div>
        <div className="col-12 col-lg-6">
          {statisticsData && <Provider 
          providerList={statisticsData.data.providers as unknown as ProvidersObject}
          defaultQueryParams={queryParams}
          updateQueryParams={updateQueryParamsAndCallAPI}
          />}
        </div>
      </div>
      <div className="row">
        <div className="mt-3">
          {statisticsData && <CaseDetail baseUrl="/regions" type="regions" searchParams={{ ...queryParams, id: Number(queryParams?.id ?? "0") }} CaseDetailListType={statisticsData.data.case_listing as unknown as CaseListingType[]} />}
        </div>
      </div>
      <div className="row">
        <div className="mt-3">
        {statisticsData && <OngoingTreatment type="regions" baseUrl="/regions" searchParams={{ ...queryParams, id: Number(queryParams?.id ?? "0") }} CaseDetailListType={statisticsData.data.ongoing_treatments as unknown as onGoingTreatmentType[]} />}
        </div>
      </div>
      <FooterText  />
    </div>

  );
};

export default Region;
